import { useId, useState } from "react";
import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import { initializePaddleClient } from "../integrations/paddle";
import { createCheckoutOrder } from "../integrations/adminApi";
import { legalVersions } from "../config/legalVersions";

const copy = {
  en: {
    loading: "Opening checkout...",
    error: "Checkout could not be opened. Please try again.",
    consentStart: "I accept the",
    terms: "Terms of service",
    consentMiddle: "and the",
    refund: "Refund policy",
    consentEnd: "and have read the",
    privacy: "Privacy policy",
    immediateRequest:
      "I expressly request that SailPlot may begin the customized implementation before the 14-day withdrawal period expires.",
    immediateAcknowledgement:
      "I understand that starting early can affect statutory withdrawal consequences and may result in proportionate payment for services already provided. Mandatory rights remain unaffected.",
  },
  de: {
    loading: "Checkout wird geöffnet...",
    error:
      "Der Checkout konnte nicht geöffnet werden. Bitte versuche es erneut.",
    consentStart: "Ich akzeptiere die",
    terms: "Nutzungsbedingungen",
    consentMiddle: "und die",
    refund: "Rückerstattungsrichtlinie",
    consentEnd: "und habe die",
    privacy: "Datenschutzerklärung",
    immediateRequest:
      "Ich verlange ausdrücklich, dass SailPlot vor Ablauf der 14-tägigen Widerrufsfrist mit der individuellen Umsetzung beginnen darf.",
    immediateAcknowledgement:
      "Mir ist bekannt, dass der vorzeitige Beginn gesetzliche Widerrufsfolgen beeinflussen und für bereits erbrachte Leistungen ein anteiliger Wertersatz entstehen kann. Zwingende Rechte bleiben unberührt.",
  },
} as const;

export function PaddleCheckoutButton({
  className,
  label,
  language,
  planId,
  priceId,
}: {
  className: string;
  label: string;
  language: SailPlotExtensionContext["language"];
  planId: string;
  priceId: string;
}) {
  const consentId = useId();
  const [accepted, setAccepted] = useState(false);
  const [immediatePerformance, setImmediatePerformance] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const text = copy[language];

  const openCheckout = async () => {
    if (!accepted) return;
    setFailed(false);
    setLoading(true);

    try {
      const checkoutId = crypto.randomUUID().replaceAll("-", "");
      const requestedDraftId = new URLSearchParams(window.location.search).get(
        "draft_id",
      );
      const draftId =
        requestedDraftId && /^[a-f0-9]{32}$/u.test(requestedDraftId)
          ? requestedDraftId
          : null;
      const order = await createCheckoutOrder(planId, checkoutId, {
        draftId,
        language,
        legalConsent: {
          ...legalVersions,
          accepted: true,
          immediatePerformanceRequested: immediatePerformance,
          immediatePerformanceAcknowledged: immediatePerformance,
        },
      });

      const paddle = await initializePaddleClient();
      if (!paddle) throw new Error("Paddle is not configured.");

      const successUrl = new URL("/order-success", window.location.origin);
      successUrl.searchParams.set("order_id", order.orderId);
      successUrl.searchParams.set("checkout_token", order.checkoutToken);
      successUrl.searchParams.set("plan", planId);
      if (draftId) successUrl.searchParams.set("draft_id", draftId);

      paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customData: { sailplot_plan: planId, order_id: order.orderId },
        settings: {
          displayMode: "overlay",
          locale: language,
          successUrl: successUrl.toString(),
          theme:
            document.documentElement.dataset.theme === "dark"
              ? "dark"
              : "light",
          variant: "one-page",
        },
      });
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="paddle-checkout-action">
      <label className="paddle-checkout-consent" htmlFor={consentId}>
        <input
          id={consentId}
          type="checkbox"
          checked={accepted}
          onChange={(event) => setAccepted(event.target.checked)}
        />
        <span>
          {text.consentStart}{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            {text.terms}
          </a>{" "}
          {text.consentMiddle}{" "}
          <a href="/refund-policy" target="_blank" rel="noopener noreferrer">
            {text.refund}
          </a>{" "}
          {text.consentEnd}{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            {text.privacy}
          </a>
          .
        </span>
      </label>
      <label className="paddle-checkout-consent">
        <input
          type="checkbox"
          checked={immediatePerformance}
          onChange={(event) => setImmediatePerformance(event.target.checked)}
        />
        <span className="paddle-performance-consent-copy">
          <span>{text.immediateRequest}</span>
          <span>{text.immediateAcknowledgement}</span>
        </span>
      </label>
      <button
        type="button"
        className={className}
        disabled={loading || !accepted}
        onClick={() => void openCheckout()}
      >
        {loading ? text.loading : label}
      </button>
      {failed && <small role="alert">{text.error}</small>}
    </div>
  );
}
